import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DEFAULT_VARIANT_TYPE,
  SHOP_KEY,
  TEXTILE_VARIANT_OPTIONS,
  buildInitialState,
  buildPayload,
  buildTextileVariantRow,
  clearDraft,
  getDraftKey,
  normalizeImageArray,
  readDraft,
  validateForm,
  writeDraft,
} from "./TextilesProductForm.shared";
import { getSafeUserMessage } from "../../../../utils/errorMessages";

function getConflictMessage(errorMeta, error) {
  if (Number(errorMeta?.status) !== 409) return "";

  switch (String(errorMeta?.code || "")) {
    case "VERSION_CONFLICT":
      return "This product was changed elsewhere. Reload before saving.";
    case "PRODUCT_SKU_EXISTS":
      return "A product or variant with this SKU already exists.";
    case "PRODUCT_SLUG_EXISTS":
      return "A product with this slug already exists in this shop.";
    case "PRODUCT_EXTERNAL_ID_EXISTS":
      return "A product with this external ID already exists in this shop.";
    default:
      return error || "Conflict detected.";
  }
}

export default function useTextilesProductForm({
  initialData,
  onSubmit,
  loading,
  error,
  errorMeta,
  saveSuccess,
  shopKey,
}) {
  const draftKey = useMemo(() => getDraftKey(initialData), [initialData]);

  const initialDraftRef = useRef(null);
  const hydratedRef = useRef(false);
  const writeTimerRef = useRef(null);

  const [form, setForm] = useState(() => {
    const savedDraft = readDraft(draftKey);
    initialDraftRef.current = savedDraft;

    return buildInitialState(initialData, savedDraft);
  });

  const [submitError, setSubmitError] = useState("");
  const [draftRestored, setDraftRestored] = useState(
    Boolean(initialDraftRef.current),
  );
  const [isDirty, setIsDirty] = useState(Boolean(initialDraftRef.current));

  useEffect(() => {
    const savedDraft = readDraft(draftKey);

    setForm(buildInitialState(initialData, savedDraft));
    setSubmitError("");
    setDraftRestored(Boolean(savedDraft));
    setIsDirty(Boolean(savedDraft));
    hydratedRef.current = true;
  }, [draftKey, initialData]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!hydratedRef.current || !isDirty) return undefined;

    if (writeTimerRef.current) {
      window.clearTimeout(writeTimerRef.current);
    }

    writeTimerRef.current = window.setTimeout(() => {
      writeDraft(draftKey, form);
      writeTimerRef.current = null;
    }, 400);

    return () => {
      if (writeTimerRef.current) {
        window.clearTimeout(writeTimerRef.current);
        writeTimerRef.current = null;
      }
    };
  }, [draftKey, form, isDirty]);

  useEffect(() => {
    if (!saveSuccess) return;

    clearDraft(draftKey);
    setDraftRestored(false);
    setIsDirty(false);
  }, [draftKey, saveSuccess]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handler = (event) => {
      if (loading || !isDirty) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [isDirty, loading]);

  const markDirty = useCallback(() => {
    setSubmitError("");
    setIsDirty(true);
  }, []);

  const setField = useCallback(
    (key, value) => {
      setForm((prev) => {
        if (Object.is(prev[key], value)) return prev;

        markDirty();
        return { ...prev, [key]: value };
      });
    },
    [markDirty],
  );

  const setVariantField = useCallback(
    (index, key, value) => {
      setForm((prev) => {
        const nextVariants = prev.variants.map((variant, currentIndex) =>
          currentIndex === index ? { ...variant, [key]: value } : variant,
        );

        markDirty();

        return {
          ...prev,
          variants: nextVariants,
        };
      });
    },
    [markDirty],
  );

  const setImages = useCallback(
    (images) => {
      setForm((prev) => {
        const nextImages = normalizeImageArray(
          typeof images === "function" ? images(prev.images) : images,
        );

        markDirty();

        return {
          ...prev,
          images: nextImages,
        };
      });
    },
    [markDirty],
  );

  const addVariant = useCallback(() => {
    setForm((prev) => {
      const usedTypes = new Set(prev.variants.map((item) => item.variantType));

      const nextOption = TEXTILE_VARIANT_OPTIONS.find(
        (option) => !option.legacy && !usedTypes.has(option.value),
      );

      if (!nextOption) return prev;

      markDirty();

      return {
        ...prev,
        variants: [
          ...prev.variants,
          buildTextileVariantRow({ variantType: nextOption.value }),
        ],
      };
    });
  }, [markDirty]);

  const removeVariant = useCallback(
    (index) => {
      setForm((prev) => {
        markDirty();

        return {
          ...prev,
          variants:
            prev.variants.length > 1
              ? prev.variants.filter(
                  (_, currentIndex) => currentIndex !== index,
                )
              : [buildTextileVariantRow({ variantType: DEFAULT_VARIANT_TYPE })],
        };
      });
    },
    [markDirty],
  );

  const discardDraft = useCallback(() => {
    clearDraft(draftKey);
    setForm(buildInitialState(initialData, null));
    setSubmitError("");
    setDraftRestored(false);
    setIsDirty(false);
  }, [draftKey, initialData]);

  const submitDisabled = Boolean(loading);

  const conflictMessage = useMemo(
    () => getConflictMessage(errorMeta, error),
    [error, errorMeta],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (loading) return;

      const validationError = validateForm(form);

      if (validationError) {
        setSubmitError(validationError);
        return;
      }

      const payload = buildPayload({
        ...form,
        shopKey: shopKey || SHOP_KEY,
      });

      try {
        await onSubmit(payload);

        clearDraft(draftKey);
        setDraftRestored(false);
        setIsDirty(false);
      } catch (submitFailure) {
        setSubmitError(
          getSafeUserMessage(
            submitFailure?.message || error,
            "We could not save this textile product right now. Please try again later.",
          ),
        );
      }
    },
    [draftKey, error, form, loading, onSubmit, shopKey],
  );

  return {
    form,
    draftRestored,
    submitError,
    submitDisabled,
    conflictMessage,
    setField,
    setVariantField,
    setImages,
    addVariant,
    removeVariant,
    discardDraft,
    handleSubmit,
  };
}
